/**
 * Utility functions for definition.ts
 */

import type { ColorMode, BadgeSize, FontFamily, FontSize } from "../types";
import {
  CUSTOMIZATION_DEFAULTS,
  CUSTOMIZATION_SETTINGS,
  PRODUCT_ATTRIBUTES,
  FONT_CDN_ROOT,
  FONT_NAME_MAP,
  SUSTAINABILITY_CSS_CLASSES,
} from "../../../../constants";

/**
 * Retrieves the target-id from the merchant-installed script tag that loaded this module.
 */
export function getTargetId(): string {
  const script = document.querySelector(
    `script[src='${import.meta.url}']`,
  ) as HTMLScriptElement | null;
  return script?.dataset.targetId || "";
}

/**
 * Get font display name from font family key
 */
export function getFontName(fontFamily: string): string {
  return (
    FONT_NAME_MAP[fontFamily as keyof typeof FONT_NAME_MAP] ||
    FONT_NAME_MAP["amazon-ember"]
  );
}

/**
 * Parse web component attributes with defaults
 */
export function parseWebComponentAttributes(target: Element) {
  const fontFamily = (target.getAttribute(CUSTOMIZATION_SETTINGS.fontFamily) ||
    CUSTOMIZATION_DEFAULTS.fontFamily) as FontFamily;
  const colorMode = (target.getAttribute(CUSTOMIZATION_SETTINGS.colorMode) ||
    CUSTOMIZATION_DEFAULTS.colorMode) as ColorMode;
  const badgeSize = (target.getAttribute(CUSTOMIZATION_SETTINGS.badgeSize) ||
    CUSTOMIZATION_DEFAULTS.badgeSize) as BadgeSize;
  const disclosureFontSize = (target.getAttribute(
    CUSTOMIZATION_SETTINGS.disclosureFontSize,
  ) || CUSTOMIZATION_DEFAULTS.disclosureFontSize) as FontSize;
  const productSku = target.getAttribute(PRODUCT_ATTRIBUTES.productSku) || null;
  const productExternalId =
    target.getAttribute(PRODUCT_ATTRIBUTES.productExternalId) || null;
  const mockRoa = target.getAttribute(PRODUCT_ATTRIBUTES.mockRoa) || undefined;
  const useMockCpfData =
    target.getAttribute(PRODUCT_ATTRIBUTES.useMockCpfData) || undefined;

  return {
    [CUSTOMIZATION_SETTINGS.fontFamily]: fontFamily,
    [CUSTOMIZATION_SETTINGS.colorMode]: colorMode,
    [CUSTOMIZATION_SETTINGS.badgeSize]: badgeSize,
    [CUSTOMIZATION_SETTINGS.disclosureFontSize]: disclosureFontSize,
    [PRODUCT_ATTRIBUTES.productSku]: productSku,
    [PRODUCT_ATTRIBUTES.productExternalId]: productExternalId,
    [PRODUCT_ATTRIBUTES.mockRoa]: mockRoa,
    [PRODUCT_ATTRIBUTES.useMockCpfData]: useMockCpfData,
  };
}

// Helper function to load font CSS into document head
export const loadFontCSS = (fontFamily: string): Promise<HTMLLinkElement> => {
  return new Promise((resolve, reject) => {
    const fontFileName = `${fontFamily}.css`;

    // Check if font is already loaded
    const existingLink = document.head.querySelector(
      `link[${SUSTAINABILITY_CSS_CLASSES.stylesDataAttribute}="${fontFileName}"]`,
    ) as HTMLLinkElement;

    if (existingLink) {
      resolve(existingLink);
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `${FONT_CDN_ROOT}${fontFileName}`;
    link.setAttribute(
      SUSTAINABILITY_CSS_CLASSES.stylesDataAttribute,
      fontFileName,
    );

    link.onload = () => resolve(link);
    link.onerror = () =>
      reject(new Error(`Failed to load font: ${fontFamily}`));

    document.head.appendChild(link);
  });
};
